import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Row,
  Col,
  message,
  Tooltip,
  Form,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

// 代理报表数据接口
interface AgentReportData {
  id: number;
  superiorAgentAccount: string; // 上级代理账号
  superiorAgentId: number; // 上级代理id
  topAgentAccount: string; // 顶层代理账号
  topAgentId: number; // 顶层代理id
  memberAccount: string; // 会员账号
  memberId: number; // 会员id
  memberType: 'agent' | 'member'; // 会员属性：agent-代理，member-普通会员
  registerTime: string; // 注册时间
  firstDepositTime?: string; // 首充时间
  subordinateAgentCount: number; // 下级代理数量
  directAgentCount: number; // 直属代理数量
  totalRecharge: number; // 总充值
  selfTotalRecharge: number; // 本人总充值
  directSubordinateTotalRecharge: number; // 直属下级总充值
  indirectSubordinateTotalRecharge: number; // 间接下级总充值
  totalWithdrawal: number; // 总提现
  selfTotalWithdrawal: number; // 本人总提现
  directSubordinateTotalWithdrawal: number; // 直属下级总提现
  indirectSubordinateTotalWithdrawal: number; // 间接下级总提现
  totalBet: number; // 总投注
  selfTotalBet: number; // 本人总投注
  directSubordinateTotalBet: number; // 直属下级总投注
  indirectSubordinateTotalBet: number; // 间接下级总投注
  validBet: number; // 有效投注
  selfValidBet: number; // 本人有效投注
  directSubordinateValidBet: number; // 直属下级有效投注
  indirectSubordinateValidBet: number; // 间接下级有效投注
  totalWinLoss: number; // 总输赢
  selfTotalWinLoss: number; // 本人总输赢
  directSubordinateTotalWinLoss: number; // 直属下级总输赢
  indirectSubordinateTotalWinLoss: number; // 间接下级总输赢
  discount: number; // 优惠
  selfDiscount: number; // 本人优惠
  directSubordinateDiscount: number; // 直属下级优惠
  indirectSubordinateDiscount: number; // 间接下级优惠
  rebate: number; // 返水
  selfRebate: number; // 本人返水
  directSubordinateRebate: number; // 直属下级返水
  indirectSubordinateRebate: number; // 间接下级返水
  issuedCommission: number; // 已发放佣金
  totalCommissionReceived: number; // 总获得返佣
  selfCommissionReceived: number; // 本人获得返佣
  directSubordinateCommissionReceived: number; // 直属下级获得返佣
  indirectSubordinateCommissionReceived: number; // 间接下级获得返佣
}

// 弹窗代理详情数据接口
interface AgentDetailData {
  id: number;
  superiorAgentAccount: string; // 上级代理账号
  superiorAgentId: number; // 上级代理id
  topAgentAccount: string; // 顶层代理账号
  topAgentId: number; // 顶层代理id
  memberAccount: string; // 会员账号
  memberId: number; // 会员id
  memberType: 'agent' | 'member'; // 会员属性：agent-代理，member-普通会员
  registerTime: string; // 注册时间
  firstDepositTime?: string; // 首充时间
  selfTotalRecharge: number; // 本人总充值
  selfTotalWithdrawal: number; // 本人总提现
  selfTotalBet: number; // 本人总投注
  selfTotalWinLoss: number; // 本人总输赢
  selfDiscount: number; // 本人优惠
  selfRebate: number; // 本人返水
  selfCommissionReceived: number; // 本人获得返佣
  issuedCommission: number; // 已发放佣金
}

// 生成弹窗代理详情数据
const generateAgentDetailData = (
  count: number, 
  clickedRecord: AgentReportData | null,
  type: 'subordinate' | 'direct'
): AgentDetailData[] => {
  if (!clickedRecord || count === 0) return [];
  
  const data: AgentDetailData[] = [];
  
  // 点击用户的顶层代理信息（所有下级都应该有相同的顶层代理）
  const topAgentAccount = clickedRecord.topAgentAccount;
  const topAgentId = clickedRecord.topAgentId;
  
  // 点击用户的账号信息（用于判断是否是直属）
  const clickedMemberId = clickedRecord.memberId;
  const clickedMemberAccount = clickedRecord.memberAccount;
  
  // 获取直属代理数量和下级代理数量
  const directCount = clickedRecord.directAgentCount;
  const subordinateCount = clickedRecord.subordinateAgentCount;
  
  if (type === 'direct') {
    // 点击"直属代理数量"时，所有记录的上级代理必须是点击的用户
    for (let i = 0; i < count; i++) {
      const hasFirstDeposit = Math.random() > 0.3;
      const registerDate = dayjs().subtract(Math.floor(Math.random() * 30), 'day');
      const firstDepositDate = hasFirstDeposit 
        ? registerDate.add(Math.floor(Math.random() * 2), 'day')
        : undefined;
      
      const hasValue = Math.random() > 0.4;
      const memberAccount = `direct${clickedMemberId}_${i + 1}`;
      const memberId = clickedMemberId * 10000 + i + 1;
      
      // 判断会员属性：有下级的才是代理
      const hasSubordinate = Math.random() > 0.8; // 20%概率有下级
      const memberType = hasSubordinate ? 'agent' : 'member';
      
      data.push({
        id: i + 1,
        superiorAgentAccount: clickedMemberAccount,
        superiorAgentId: clickedMemberId,
        topAgentAccount: topAgentAccount,
        topAgentId: topAgentId,
        memberAccount: memberAccount,
        memberId: memberId,
        memberType: memberType,
        registerTime: registerDate.format('YYYY-MM-DD HH:mm:ss'),
        firstDepositTime: firstDepositDate?.format('YYYY-MM-DD HH:mm:ss'),
        selfTotalRecharge: hasValue ? Math.floor(Math.random() * 500000) : 0,
        selfTotalWithdrawal: hasValue ? Math.floor(Math.random() * 200000) : 0,
        selfTotalBet: hasValue ? Math.floor(Math.random() * 1500000) : 0,
        selfTotalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 50000) : 0,
        selfDiscount: hasValue ? Math.floor(Math.random() * 50000) : 0,
        selfRebate: hasValue ? Math.floor(Math.random() * 5000) : 0,
        selfCommissionReceived: hasValue ? Math.floor(Math.random() * 40000) : 0,
        issuedCommission: hasValue ? Math.floor(Math.random() * 50000) : 0,
      });
    }
  } else {
    // 点击"下级代理数量"时
    // 前directCount个是直属下级（上级是点击的用户）
    // 剩下的(count - directCount)个是间接下级（上级是点击用户的直属下级之一）
    
    // 先创建直属下级
    const directSubordinates: AgentDetailData[] = [];
    for (let i = 0; i < directCount; i++) {
      const hasFirstDeposit = Math.random() > 0.3;
      const registerDate = dayjs().subtract(Math.floor(Math.random() * 30), 'day');
      const firstDepositDate = hasFirstDeposit 
        ? registerDate.add(Math.floor(Math.random() * 2), 'day')
        : undefined;
      
      const hasValue = Math.random() > 0.4;
      const memberAccount = `direct${clickedMemberId}_${i + 1}`;
      const memberId = clickedMemberId * 10000 + i + 1;
      
      // 判断会员属性：有下级的才是代理
      const hasSubordinate = Math.random() > 0.8;
      const memberType = hasSubordinate ? 'agent' : 'member';
      
      const directSub: AgentDetailData = {
        id: i + 1,
        superiorAgentAccount: clickedMemberAccount,
        superiorAgentId: clickedMemberId,
        topAgentAccount: topAgentAccount,
        topAgentId: topAgentId,
        memberAccount: memberAccount,
        memberId: memberId,
        memberType: memberType,
        registerTime: registerDate.format('YYYY-MM-DD HH:mm:ss'),
        firstDepositTime: firstDepositDate?.format('YYYY-MM-DD HH:mm:ss'),
        selfTotalRecharge: hasValue ? Math.floor(Math.random() * 500000) : 0,
        selfTotalWithdrawal: hasValue ? Math.floor(Math.random() * 200000) : 0,
        selfTotalBet: hasValue ? Math.floor(Math.random() * 1500000) : 0,
        selfTotalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 50000) : 0,
        selfDiscount: hasValue ? Math.floor(Math.random() * 50000) : 0,
        selfRebate: hasValue ? Math.floor(Math.random() * 5000) : 0,
        selfCommissionReceived: hasValue ? Math.floor(Math.random() * 40000) : 0,
        issuedCommission: hasValue ? Math.floor(Math.random() * 50000) : 0,
      };
      
      directSubordinates.push(directSub);
      data.push(directSub);
    }
    
    // 再创建间接下级（上级是直属下级之一）
    const indirectCount = count - directCount;
    for (let i = 0; i < indirectCount; i++) {
      const hasFirstDeposit = Math.random() > 0.3;
      const registerDate = dayjs().subtract(Math.floor(Math.random() * 30), 'day');
      const firstDepositDate = hasFirstDeposit 
        ? registerDate.add(Math.floor(Math.random() * 2), 'day')
        : undefined;
      
      const hasValue = Math.random() > 0.4;
      
      // 从直属下级中随机选择一个作为上级
      const superiorIndex = Math.floor(Math.random() * directSubordinates.length);
      const superior = directSubordinates[superiorIndex];
      
      const memberAccount = `indirect${clickedMemberId}_${i + 1}`;
      const memberId = clickedMemberId * 10000 + directCount + i + 1;
      
      // 判断会员属性：有下级的才是代理
      const hasSubordinate = Math.random() > 0.8;
      const memberType = hasSubordinate ? 'agent' : 'member';
      
      data.push({
        id: directCount + i + 1,
        superiorAgentAccount: superior.memberAccount,
        superiorAgentId: superior.memberId,
        topAgentAccount: topAgentAccount,
        topAgentId: topAgentId,
        memberAccount: memberAccount,
        memberId: memberId,
        memberType: memberType,
        registerTime: registerDate.format('YYYY-MM-DD HH:mm:ss'),
        firstDepositTime: firstDepositDate?.format('YYYY-MM-DD HH:mm:ss'),
        selfTotalRecharge: hasValue ? Math.floor(Math.random() * 500000) : 0,
        selfTotalWithdrawal: hasValue ? Math.floor(Math.random() * 200000) : 0,
        selfTotalBet: hasValue ? Math.floor(Math.random() * 1500000) : 0,
        selfTotalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 50000) : 0,
        selfDiscount: hasValue ? Math.floor(Math.random() * 50000) : 0,
        selfRebate: hasValue ? Math.floor(Math.random() * 5000) : 0,
        selfCommissionReceived: hasValue ? Math.floor(Math.random() * 40000) : 0,
        issuedCommission: hasValue ? Math.floor(Math.random() * 50000) : 0,
      });
    }
  }
  
  return data;
};

// 生成随机数据
const generateRandomData = (): AgentReportData[] => {
  const accounts = [
    'qudao0014', 'qudao0015', 'qudao0016', 'qudao0017', 'angletest123',
    'daili1004', 'kenji99111', 'Test4800', 'system', 'agent001',
    'agent002', 'agent003', 'agent004', 'agent005', 'agent006',
    'agent007', 'agent008', 'agent009', 'agent010', 'agent011',
    '1FA5sqc1o1XD', 'ONZ3ej44oQf8', 'test001', 'test002', 'test003',
  ];
  
  const data: AgentReportData[] = [];
  
  for (let i = 0; i < 59; i++) {
    const superiorAccount = accounts[Math.floor(Math.random() * accounts.length)];
    const topAccount = accounts[Math.floor(Math.random() * accounts.length)];
    const memberAccount = accounts[Math.floor(Math.random() * accounts.length)];
    
    const hasFirstDeposit = Math.random() > 0.3; // 70%有首充
    const registerDate = dayjs().subtract(Math.floor(Math.random() * 30), 'day');
    const firstDepositDate = hasFirstDeposit 
      ? registerDate.add(Math.floor(Math.random() * 2), 'day')
      : undefined;
    
    // 生成随机数值，有些为0，有些有值
    const hasValue = Math.random() > 0.4; // 60%有值
    // 先确定直属代理数量
    const directCount = hasValue ? Math.floor(Math.random() * 5) : 0;
    // 下级代理数量一定 >= 直属代理数量
    // 如果直属代理数量是0，下级代理数量也是0
    // 如果直属代理数量 > 0，下级代理数量 = 直属代理数量 + 间接下级数量（0到5个）
    const indirectCount = directCount > 0 ? Math.floor(Math.random() * 5) : 0;
    const subordinateCount = directCount + indirectCount;
    // 根据是否有下级判断会员属性：有下级的是代理，没有的是普通会员
    const memberType = subordinateCount > 0 ? 'agent' : 'member';
    
    data.push({
      id: i + 1,
      superiorAgentAccount: superiorAccount,
      superiorAgentId: 259198 + i,
      topAgentAccount: topAccount,
      topAgentId: 257969 + i,
      memberAccount: memberAccount,
      memberId: 259200 + i,
      memberType: memberType,
      registerTime: registerDate.format('YYYY-MM-DD HH:mm:ss'),
      firstDepositTime: firstDepositDate?.format('YYYY-MM-DD HH:mm:ss'),
      subordinateAgentCount: subordinateCount,
      directAgentCount: directCount,
      totalRecharge: hasValue ? Math.floor(Math.random() * 1000000) : 0,
      selfTotalRecharge: hasValue ? Math.floor(Math.random() * 500000) : 0,
      directSubordinateTotalRecharge: hasValue ? Math.floor(Math.random() * 300000) : 0,
      indirectSubordinateTotalRecharge: hasValue ? Math.floor(Math.random() * 200000) : 0,
      totalWithdrawal: hasValue ? Math.floor(Math.random() * 500000) : 0,
      selfTotalWithdrawal: hasValue ? Math.floor(Math.random() * 200000) : 0,
      directSubordinateTotalWithdrawal: hasValue ? Math.floor(Math.random() * 150000) : 0,
      indirectSubordinateTotalWithdrawal: hasValue ? Math.floor(Math.random() * 100000) : 0,
      totalBet: hasValue ? Math.floor(Math.random() * 3000000) : 0,
      selfTotalBet: hasValue ? Math.floor(Math.random() * 1500000) : 0,
      directSubordinateTotalBet: hasValue ? Math.floor(Math.random() * 800000) : 0,
      indirectSubordinateTotalBet: hasValue ? Math.floor(Math.random() * 500000) : 0,
      validBet: hasValue ? Math.floor(Math.random() * 2800000) : 0,
      selfValidBet: hasValue ? Math.floor(Math.random() * 1400000) : 0,
      directSubordinateValidBet: hasValue ? Math.floor(Math.random() * 750000) : 0,
      indirectSubordinateValidBet: hasValue ? Math.floor(Math.random() * 450000) : 0,
      totalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 100000) : 0,
      selfTotalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 50000) : 0,
      directSubordinateTotalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 30000) : 0,
      indirectSubordinateTotalWinLoss: hasValue ? (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 20000) : 0,
      discount: hasValue ? Math.floor(Math.random() * 100000) : 0,
      selfDiscount: hasValue ? Math.floor(Math.random() * 50000) : 0,
      directSubordinateDiscount: hasValue ? Math.floor(Math.random() * 30000) : 0,
      indirectSubordinateDiscount: hasValue ? Math.floor(Math.random() * 20000) : 0,
      rebate: hasValue ? Math.floor(Math.random() * 10000) : 0,
      selfRebate: hasValue ? Math.floor(Math.random() * 5000) : 0,
      directSubordinateRebate: hasValue ? Math.floor(Math.random() * 3000) : 0,
      indirectSubordinateRebate: hasValue ? Math.floor(Math.random() * 2000) : 0,
      issuedCommission: hasValue ? Math.floor(Math.random() * 50000) : 0,
      totalCommissionReceived: hasValue ? Math.floor(Math.random() * 80000) : 0,
      selfCommissionReceived: hasValue ? Math.floor(Math.random() * 40000) : 0,
      directSubordinateCommissionReceived: hasValue ? Math.floor(Math.random() * 25000) : 0,
      indirectSubordinateCommissionReceived: hasValue ? Math.floor(Math.random() * 15000) : 0,
    });
  }
  
  // 添加一些特殊数据，模拟截图中的数据
  data[0] = {
    ...data[0],
    superiorAgentAccount: 'qudao0014',
    superiorAgentId: 259198,
    topAgentAccount: '1FA5sqc1o1XD',
    topAgentId: 257969,
    memberAccount: 'ONZ3ej44oQf8',
    memberId: 259202,
    memberType: 'member', // 没有下级，是普通会员
    registerTime: '2025-12-16 16:19:42',
    firstDepositTime: undefined,
    subordinateAgentCount: 0,
    directAgentCount: 0,
  };
  
  data[1] = {
    ...data[1],
    superiorAgentAccount: 'qudao0014',
    superiorAgentId: 259198,
    topAgentAccount: '1FA5sqc1o1XD',
    topAgentId: 257969,
    memberAccount: 'qudao0017',
    memberId: 259201,
    memberType: 'member', // 没有下级，是普通会员
    registerTime: '2025-12-16 15:45:53',
    firstDepositTime: undefined,
    subordinateAgentCount: 0,
    directAgentCount: 0,
  };
  
  data[2] = {
    ...data[2],
    superiorAgentAccount: 'qudao0015',
    superiorAgentId: 259199,
    topAgentAccount: '1FA5sqc1o1XD',
    topAgentId: 257969,
    memberAccount: 'qudao0016',
    memberId: 259200,
    memberType: 'member', // 没有下级，是普通会员
    registerTime: '2025-12-16 15:38:59',
    firstDepositTime: undefined,
    subordinateAgentCount: 0,
    directAgentCount: 0,
  };
  
  data[3] = {
    ...data[3],
    superiorAgentAccount: 'qudao0014',
    superiorAgentId: 259198,
    topAgentAccount: '1FA5sqc1o1XD',
    topAgentId: 257969,
    memberAccount: 'qudao0015',
    memberId: 259199,
    memberType: 'agent', // 有下级，是代理
    registerTime: '2025-12-16 15:15:41',
    firstDepositTime: undefined,
    directAgentCount: 1,
    subordinateAgentCount: 1, // 下级数量 = 直属数量（没有间接下级）
  };
  
  data[4] = {
    ...data[4],
    superiorAgentAccount: 'angletest123',
    superiorAgentId: 257963,
    topAgentAccount: '1FA5sqc1o1XD',
    topAgentId: 257969,
    memberAccount: 'qudao0014',
    memberId: 259198,
    memberType: 'agent', // 有下级，是代理
    registerTime: '2025-12-16 14:42:49',
    firstDepositTime: undefined,
    directAgentCount: 3, // 3个直属下级
    subordinateAgentCount: 4, // 4个下级（3个直属 + 1个间接）
  };
  
  data[7] = {
    ...data[7],
    superiorAgentAccount: 'daili1004',
    superiorAgentId: 258735,
    topAgentAccount: '1FA5sqc1o1XD',
    topAgentId: 257969,
    memberAccount: 'kenji99111',
    memberId: 259195,
    registerTime: '2025-12-15 13:10:20',
    firstDepositTime: '2025-12-15 13:10:34',
    totalRecharge: 430000,
    selfTotalRecharge: 430000,
    totalBet: 2205000,
    selfTotalBet: 2205000,
    validBet: 2171940,
    selfValidBet: 2171940,
    totalWinLoss: -34060,
    selfTotalWinLoss: -34060,
    rebate: 9773.73,
    selfRebate: 9773.73,
  };
  
  data[8] = {
    ...data[8],
    superiorAgentAccount: 'system',
    superiorAgentId: 0,
    topAgentAccount: 'Test4800',
    topAgentId: 259194,
    memberAccount: 'Test4800',
    memberId: 259194,
    registerTime: '2025-12-13 17:57:31',
    firstDepositTime: '2025-12-13 17:58:47',
    totalRecharge: 500000,
    selfTotalRecharge: 500000,
    totalBet: 418000,
    selfTotalBet: 418000,
    validBet: 340700,
    selfValidBet: 340700,
    totalWinLoss: 24100,
    selfTotalWinLoss: 24100,
    discount: 64000,
    selfDiscount: 64000,
    rebate: 1703.5,
    selfRebate: 1703.5,
  };
  
  // 添加更多有数据值的记录
  data[9] = {
    ...data[9],
    totalBet: 40000,
    selfTotalBet: 40000,
    validBet: 34700,
    selfValidBet: 34700,
    totalWinLoss: -15300,
    selfTotalWinLoss: -15300,
    discount: 1000,
    selfDiscount: 1000,
    rebate: 121.45,
    selfRebate: 121.45,
  };
  
  // 添加一些有大量优惠的记录
  data[10] = {
    ...data[10],
    discount: 99999000,
    selfDiscount: 99999000,
  };
  
  data[11] = {
    ...data[11],
    discount: 99999000,
    directSubordinateDiscount: 99999000,
  };
  
  return data;
};

const AgentReportRefactored: React.FC = () => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const initialData = generateRandomData();
  const [dataSource, setDataSource] = useState<AgentReportData[]>(initialData);
  const [filteredDataSource, setFilteredDataSource] = useState<AgentReportData[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: initialData.length,
  });
  
  // 弹窗相关状态
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'subordinate' | 'direct'>('subordinate');
  const [modalTitle, setModalTitle] = useState('');
  const [clickedRecord, setClickedRecord] = useState<AgentReportData | null>(null);
  const [modalDataSource, setModalDataSource] = useState<AgentDetailData[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPagination, setModalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 处理搜索
  const handleSearch = () => {
    setLoading(true);
    const values = form.getFieldsValue();
    let filtered = [...dataSource];
    
    // 根据会员属性筛选
    if (values.memberType && values.memberType !== 'all') {
      filtered = filtered.filter(item => item.memberType === values.memberType);
    }
    
    // 根据其他条件筛选（这里可以扩展更多筛选条件）
    if (values.memberId) {
      filtered = filtered.filter(item => 
        item.memberId.toString().includes(values.memberId.toString())
      );
    }
    
    if (values.memberAccount) {
      filtered = filtered.filter(item => 
        item.memberAccount.toLowerCase().includes(values.memberAccount.toLowerCase())
      );
    }
    
    if (values.superiorAgentAccountId) {
      filtered = filtered.filter(item => 
        item.superiorAgentId.toString().includes(values.superiorAgentAccountId.toString())
      );
    }
    
    if (values.superiorAgentAccount) {
      filtered = filtered.filter(item => 
        item.superiorAgentAccount.toLowerCase().includes(values.superiorAgentAccount.toLowerCase())
      );
    }
    
    if (values.topAgentAccountId) {
      filtered = filtered.filter(item => 
        item.topAgentId.toString().includes(values.topAgentAccountId.toString())
      );
    }
    
    if (values.topAgentAccount) {
      filtered = filtered.filter(item => 
        item.topAgentAccount.toLowerCase().includes(values.topAgentAccount.toLowerCase())
      );
    }
    
    // 立即更新数据
    setFilteredDataSource(filtered);
    setPagination({
      current: 1,
      pageSize: pagination.pageSize,
      total: filtered.length,
    });
    
    // 模拟搜索延迟
    setTimeout(() => {
      setLoading(false);
      message.success(`搜索完成，找到 ${filtered.length} 条记录`);
    }, 300);
  };

  // 处理重置
  const handleReset = () => {
    form.resetFields();
    // 重置后恢复所有数据
    setFilteredDataSource(dataSource);
    setPagination({
      current: 1,
      pageSize: pagination.pageSize,
      total: dataSource.length,
    });
    message.info('已重置');
  };

  // 处理导出Excel
  const handleExportExcel = () => {
    message.success('Excel导出功能开发中...');
  };

  // 处理点击下级代理数量或直属代理数量
  const handleAgentCountClick = (record: AgentReportData, type: 'subordinate' | 'direct') => {
    const count = type === 'subordinate' ? record.subordinateAgentCount : record.directAgentCount;
    if (count === 0) return;
    
    setModalType(type);
    setModalTitle(type === 'subordinate' ? '下级代理数量' : '直属代理数量');
    setClickedRecord(record);
    
    // 生成弹窗数据（传入点击的记录和类型）
    const detailData = generateAgentDetailData(count, record, type);
    setModalDataSource(detailData);
    setModalPagination({
      current: 1,
      pageSize: 10,
      total: detailData.length,
    });
    
    // 设置表单初始值
    setTimeout(() => {
      modalForm.setFieldsValue({
        superiorAgentAccountId: type === 'direct' ? record.memberId : undefined,
        memberType: 'all',
      });
    }, 0);
    
    setModalVisible(true);
  };

  // 处理弹窗搜索
  const handleModalSearch = () => {
    setModalLoading(true);
    const values = modalForm.getFieldsValue();
    
    // 重新生成完整数据（模拟从服务器获取）
    const count = modalType === 'subordinate' ? clickedRecord?.subordinateAgentCount || 0 : clickedRecord?.directAgentCount || 0;
    let allData: AgentDetailData[] = [];
    if (clickedRecord) {
      allData = generateAgentDetailData(count, clickedRecord, modalType);
    }
    let filtered = [...allData];
    
    // 根据会员属性筛选
    if (values.memberType && values.memberType !== 'all') {
      filtered = filtered.filter(item => item.memberType === values.memberType);
    }
    
    // 根据其他条件筛选
    if (values.memberId) {
      filtered = filtered.filter(item => 
        item.memberId.toString().includes(values.memberId.toString())
      );
    }
    
    if (values.memberAccount) {
      filtered = filtered.filter(item => 
        item.memberAccount.toLowerCase().includes(values.memberAccount.toLowerCase())
      );
    }
    
    if (values.superiorAgentAccountId) {
      filtered = filtered.filter(item => 
        item.superiorAgentId.toString().includes(values.superiorAgentAccountId.toString())
      );
    }
    
    if (values.superiorAgentAccount) {
      filtered = filtered.filter(item => 
        item.superiorAgentAccount.toLowerCase().includes(values.superiorAgentAccount.toLowerCase())
      );
    }
    
    // 立即更新数据
    setModalDataSource(filtered);
    setModalPagination({
      current: 1,
      pageSize: modalPagination.pageSize,
      total: filtered.length,
    });
    
    setTimeout(() => {
      setModalLoading(false);
      message.success(`搜索完成，找到 ${filtered.length} 条记录`);
    }, 300);
  };

  // 处理弹窗重置
  const handleModalReset = () => {
    modalForm.resetFields();
    // 重新生成数据
    const count = modalType === 'subordinate' ? clickedRecord?.subordinateAgentCount || 0 : clickedRecord?.directAgentCount || 0;
    if (clickedRecord) {
      const detailData = generateAgentDetailData(count, clickedRecord, modalType);
      setModalDataSource(detailData);
      setModalPagination({
        current: 1,
        pageSize: 10,
        total: detailData.length,
      });
      
      // 设置表单初始值
      modalForm.setFieldsValue({
        superiorAgentAccountId: modalType === 'direct' ? clickedRecord.memberId : undefined,
        memberType: 'all',
      });
    }
    message.info('已重置');
  };

  // 处理弹窗导出Excel
  const handleModalExportExcel = () => {
    message.success('Excel导出功能开发中...');
  };

  // 关闭弹窗
  const handleModalClose = () => {
    setModalVisible(false);
    setClickedRecord(null);
    modalForm.resetFields();
  };

  // 格式化数字
  const formatNumber = (num: number) => {
    if (num === 0) return '0';
    return num.toLocaleString('zh-CN');
  };

  // 表格列定义
  const columns = [
    {
      title: '上级代理账号',
      dataIndex: 'superiorAgentAccount',
      key: 'superiorAgentAccount',
      width: 150,
      fixed: 'left' as const,
      sorter: true,
    },
    {
      title: '上级代理id',
      dataIndex: 'superiorAgentId',
      key: 'superiorAgentId',
      width: 120,
      sorter: true,
    },
    {
      title: '顶层代理账号',
      dataIndex: 'topAgentAccount',
      key: 'topAgentAccount',
      width: 150,
      sorter: true,
    },
    {
      title: '顶层代理id',
      dataIndex: 'topAgentId',
      key: 'topAgentId',
      width: 120,
      sorter: true,
    },
    {
      title: '会员账号',
      dataIndex: 'memberAccount',
      key: 'memberAccount',
      width: 150,
      sorter: true,
    },
    {
      title: '会员id',
      dataIndex: 'memberId',
      key: 'memberId',
      width: 120,
      sorter: true,
    },
    {
      title: '会员属性',
      dataIndex: 'memberType',
      key: 'memberType',
      width: 100,
      sorter: true,
      render: (type: 'agent' | 'member') => {
        return type === 'agent' ? (
          <span style={{ color: '#1890ff' }}>代理</span>
        ) : (
          <span style={{ color: '#52c41a' }}>普通会员</span>
        );
      },
    },
    {
      title: '注册时间',
      dataIndex: 'registerTime',
      key: 'registerTime',
      width: 180,
      sorter: true,
    },
    {
      title: '首充时间',
      dataIndex: 'firstDepositTime',
      key: 'firstDepositTime',
      width: 180,
      sorter: true,
      render: (text: string) => text || '-',
    },
    {
      title: (
        <span>
          下级代理数量
          <Tooltip title="包含所有下级代理的数量（直属+间接）">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'subordinateAgentCount',
      key: 'subordinateAgentCount',
      width: 130,
      sorter: true,
      render: (value: number, record: AgentReportData) => {
        if (value === 0) {
          return '0';
        }
        return (
          <a
            onClick={() => handleAgentCountClick(record, 'subordinate')}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {formatNumber(value)}
          </a>
        );
      },
    },
    {
      title: (
        <span>
          直属代理数量
          <Tooltip title="直接下级代理的数量">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directAgentCount',
      key: 'directAgentCount',
      width: 130,
      sorter: true,
      render: (value: number, record: AgentReportData) => {
        if (value === 0) {
          return '0';
        }
        return (
          <a
            onClick={() => handleAgentCountClick(record, 'direct')}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {formatNumber(value)}
          </a>
        );
      },
    },
    {
      title: (
        <span>
          总充值
          <Tooltip title="包含代理自身+所有下级充值数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalRecharge',
      key: 'totalRecharge',
      width: 120,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          本人总充值
          <Tooltip title="代理本人账户的总充值金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfTotalRecharge',
      key: 'selfTotalRecharge',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          直属下级总充值
          <Tooltip title="所有直属下级代理的总充值金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateTotalRecharge',
      key: 'directSubordinateTotalRecharge',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          间接下级总充值
          <Tooltip title="所有间接下级代理的总充值金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateTotalRecharge',
      key: 'indirectSubordinateTotalRecharge',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          总提现
          <Tooltip title="包含代理自身+所有下级提现数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalWithdrawal',
      key: 'totalWithdrawal',
      width: 120,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          本人总提现
          <Tooltip title="代理本人账户的总提现金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfTotalWithdrawal',
      key: 'selfTotalWithdrawal',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          直属下级总提现
          <Tooltip title="所有直属下级代理的总提现金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateTotalWithdrawal',
      key: 'directSubordinateTotalWithdrawal',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          间接下级总提现
          <Tooltip title="所有间接下级代理的总提现金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateTotalWithdrawal',
      key: 'indirectSubordinateTotalWithdrawal',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          总投注
          <Tooltip title="包含代理自身+所有下级投注数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalBet',
      key: 'totalBet',
      width: 120,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          本人总投注
          <Tooltip title="代理本人账户的总投注金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfTotalBet',
      key: 'selfTotalBet',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          直属下级总投注
          <Tooltip title="所有直属下级代理的总投注金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateTotalBet',
      key: 'directSubordinateTotalBet',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          间接下级总投注
          <Tooltip title="所有间接下级代理的总投注金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateTotalBet',
      key: 'indirectSubordinateTotalBet',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          有效投注
          <Tooltip title="包含代理自身+所有下级有效投注数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'validBet',
      key: 'validBet',
      width: 120,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          本人有效投注
          <Tooltip title="代理本人账户的有效投注金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfValidBet',
      key: 'selfValidBet',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          直属下级有效投注
          <Tooltip title="所有直属下级代理的有效投注金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateValidBet',
      key: 'directSubordinateValidBet',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          间接下级有效投注
          <Tooltip title="所有间接下级代理的有效投注金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateValidBet',
      key: 'indirectSubordinateValidBet',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          总输赢
          <Tooltip title="包含代理自身+所有下级输赢数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalWinLoss',
      key: 'totalWinLoss',
      width: 120,
      sorter: true,
      render: (value: number) => {
        const formatted = formatNumber(Math.abs(value));
        return value >= 0 ? formatted : `-${formatted}`;
      },
    },
    {
      title: (
        <span>
          本人总输赢
          <Tooltip title="代理本人账户的总输赢金额（正数为盈利，负数为亏损）">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfTotalWinLoss',
      key: 'selfTotalWinLoss',
      width: 130,
      sorter: true,
      render: (value: number) => {
        const formatted = formatNumber(Math.abs(value));
        return value >= 0 ? formatted : `-${formatted}`;
      },
    },
    {
      title: (
        <span>
          直属下级总输赢
          <Tooltip title="所有直属下级代理的总输赢金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateTotalWinLoss',
      key: 'directSubordinateTotalWinLoss',
      width: 150,
      sorter: true,
      render: (value: number) => {
        const formatted = formatNumber(Math.abs(value));
        return value >= 0 ? formatted : `-${formatted}`;
      },
    },
    {
      title: (
        <span>
          间接下级总输赢
          <Tooltip title="所有间接下级代理的总输赢金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateTotalWinLoss',
      key: 'indirectSubordinateTotalWinLoss',
      width: 150,
      sorter: true,
      render: (value: number) => {
        const formatted = formatNumber(Math.abs(value));
        return value >= 0 ? formatted : `-${formatted}`;
      },
    },
    {
      title: (
        <span>
          优惠
          <Tooltip title="包含代理自身+所有下级优惠数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'discount',
      key: 'discount',
      width: 120,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          本人优惠
          <Tooltip title="代理本人账户获得的优惠金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfDiscount',
      key: 'selfDiscount',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          直属下级优惠
          <Tooltip title="所有直属下级代理获得的优惠金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateDiscount',
      key: 'directSubordinateDiscount',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          间接下级优惠
          <Tooltip title="所有间接下级代理获得的优惠金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateDiscount',
      key: 'indirectSubordinateDiscount',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          返水
          <Tooltip title="包含代理自身+所有下级返水数据">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'rebate',
      key: 'rebate',
      width: 120,
      sorter: true,
      render: (value: number) => {
        if (value === 0) return '0';
        return value.toFixed(2);
      },
    },
    {
      title: (
        <span>
          本人返水
          <Tooltip title="代理本人账户获得的返水金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfRebate',
      key: 'selfRebate',
      width: 130,
      sorter: true,
      render: (value: number) => {
        if (value === 0) return '0';
        return value.toFixed(2);
      },
    },
    {
      title: (
        <span>
          直属下级返水
          <Tooltip title="所有直属下级代理获得的返水金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateRebate',
      key: 'directSubordinateRebate',
      width: 150,
      sorter: true,
      render: (value: number) => {
        if (value === 0) return '0';
        return value.toFixed(2);
      },
    },
    {
      title: (
        <span>
          间接下级返水
          <Tooltip title="所有间接下级代理获得的返水金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateRebate',
      key: 'indirectSubordinateRebate',
      width: 150,
      sorter: true,
      render: (value: number) => {
        if (value === 0) return '0';
        return value.toFixed(2);
      },
    },
    {
      title: (
        <span>
          已发放佣金
          <Tooltip title="已经发放给代理的佣金总额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'issuedCommission',
      key: 'issuedCommission',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          总获得返佣
          <Tooltip title="包含代理自身+所有下级获得的返佣总额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'totalCommissionReceived',
      key: 'totalCommissionReceived',
      width: 130,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          本人获得返佣
          <Tooltip title="代理本人账户获得的返佣金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'selfCommissionReceived',
      key: 'selfCommissionReceived',
      width: 150,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          直属下级获得返佣
          <Tooltip title="所有直属下级代理获得的返佣金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'directSubordinateCommissionReceived',
      key: 'directSubordinateCommissionReceived',
      width: 180,
      sorter: true,
      render: formatNumber,
    },
    {
      title: (
        <span>
          间接下级获得返佣
          <Tooltip title="所有间接下级代理获得的返佣金额">
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'indirectSubordinateCommissionReceived',
      key: 'indirectSubordinateCommissionReceived',
      width: 180,
      sorter: true,
      render: formatNumber,
    },
  ];

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        {/* 查询条件区域 */}
        <Form form={form}>
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]}>
            {/* 第一行 */}
            <Col span={8}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                  注册时间：
                </span>
                <div style={{ flex: 1, display: 'flex', gap: 8 }}>
                  <DatePicker
                    placeholder="开始日期"
                    style={{ flex: 1 }}
                    format="YYYY-MM-DD"
                  />
                  <DatePicker
                    placeholder="结束日期"
                    style={{ flex: 1 }}
                    format="YYYY-MM-DD"
                  />
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                  首充时间：
                </span>
                <div style={{ flex: 1, display: 'flex', gap: 8 }}>
                  <DatePicker
                    placeholder="开始日期"
                    style={{ flex: 1 }}
                    format="YYYY-MM-DD"
                  />
                  <DatePicker
                    placeholder="结束日期"
                    style={{ flex: 1 }}
                    format="YYYY-MM-DD"
                  />
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                  数据时间：
                </span>
                <RangePicker
                  style={{ flex: 1 }}
                  defaultValue={[dayjs().subtract(7, 'day'), dayjs()]}
                  format="YYYY-MM-DD"
                />
              </div>
            </Col>
            <Col span={8}>
              <Form.Item name="memberId" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    会员ID：
                  </span>
                  <Input
                    placeholder="请输入会员ID"
                    style={{ flex: 1 }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="memberAccount" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    会员账号：
                  </span>
                  <Input
                    placeholder="请输入会员账号"
                    style={{ flex: 1 }}
                  />
                </div>
              </Form.Item>
            </Col>
            {/* 第二行 */}
            <Col span={8}>
              <Form.Item name="superiorAgentAccountId" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    上级代理账号ID：
                  </span>
                  <Input
                    placeholder="请输入上级代理账号ID"
                    style={{ flex: 1 }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="superiorAgentAccount" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    上级代理账号：
                  </span>
                  <Input
                    placeholder="请输入上级代理账号"
                    style={{ flex: 1 }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="topAgentAccountId" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    顶层代理账号ID：
                  </span>
                  <Input
                    placeholder="请输入顶层代理账号ID"
                    style={{ flex: 1 }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="topAgentAccount" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    顶层代理账号：
                  </span>
                  <Input
                    placeholder="请输入顶层代理账号"
                    style={{ flex: 1 }}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={8}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                  是否过滤测试账号：
                </span>
                <Select
                  defaultValue="是"
                  style={{ flex: 1 }}
                >
                  <Option value="是">是</Option>
                  <Option value="否">否</Option>
                </Select>
              </div>
            </Col>
            <Col span={8}>
              <Form.Item name="memberType" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    会员属性：
                  </span>
                  <Select
                    defaultValue="all"
                    style={{ flex: 1 }}
                  >
                    <Option value="all">全部</Option>
                    <Option value="agent">代理</Option>
                    <Option value="member">普通会员</Option>
                  </Select>
                </div>
              </Form.Item>
            </Col>
          </Row>
          
          {/* 操作按钮 */}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                搜索
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
              >
                重置
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportExcel}
              >
                下载Excel
              </Button>
            </Space>
          </div>
          </div>
        </Form>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={filteredDataSource}
          rowKey="id"
          loading={loading}
          scroll={{ x: 6000 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共${total}条`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setPagination({
                ...pagination,
                current: page,
                pageSize: pageSize || 50,
              });
            },
          }}
        />
      </Card>

      {/* 代理详情弹窗 */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={1400}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', padding: '24px' }}
      >
        <Form form={modalForm}>
          {/* 弹窗查询条件区域 */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]}>
              <Col span={8}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    注册时间：
                  </span>
                  <div style={{ flex: 1, display: 'flex', gap: 8 }}>
                    <DatePicker
                      placeholder="开始日期"
                      style={{ flex: 1 }}
                      format="YYYY-MM-DD"
                    />
                    <DatePicker
                      placeholder="结束日期"
                      style={{ flex: 1 }}
                      format="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    首充时间：
                  </span>
                  <div style={{ flex: 1, display: 'flex', gap: 8 }}>
                    <DatePicker
                      placeholder="开始日期"
                      style={{ flex: 1 }}
                      format="YYYY-MM-DD"
                    />
                    <DatePicker
                      placeholder="结束日期"
                      style={{ flex: 1 }}
                      format="YYYY-MM-DD"
                    />
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    数据时间：
                  </span>
                  <RangePicker
                    style={{ flex: 1 }}
                    defaultValue={[dayjs().subtract(7, 'day'), dayjs()]}
                    format="YYYY-MM-DD"
                  />
                </div>
              </Col>
              <Col span={8}>
                <Form.Item name="memberId" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                      会员ID：
                    </span>
                    <Input
                      placeholder="请输入会员ID"
                      style={{ flex: 1 }}
                    />
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="memberAccount" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                      会员账号：
                    </span>
                    <Input
                      placeholder="请输入会员账号"
                      style={{ flex: 1 }}
                    />
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="superiorAgentAccountId" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                      上级代理账号ID：
                    </span>
                    <Input
                      placeholder="请输入上级代理账号ID"
                      style={{ flex: 1 }}
                    />
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="superiorAgentAccount" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                      上级代理账号：
                    </span>
                    <Input
                      placeholder="请输入上级代理账号"
                      style={{ flex: 1 }}
                    />
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="memberType" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 90, textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                      会员属性：
                    </span>
                    <Select
                      defaultValue="all"
                      style={{ flex: 1 }}
                    >
                      <Option value="all">全部</Option>
                      <Option value="agent">代理</Option>
                      <Option value="member">普通会员</Option>
                    </Select>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            
            {/* 操作按钮 */}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleModalSearch}
                  loading={modalLoading}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleModalReset}
                >
                  重置
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleModalExportExcel}
                >
                  下载Excel
                </Button>
              </Space>
            </div>
          </div>
        </Form>

        {/* 弹窗数据表格 */}
        <div>
          <Table
          columns={[
              {
                title: '上级代理账号',
                dataIndex: 'superiorAgentAccount',
                key: 'superiorAgentAccount',
                width: 150,
                fixed: 'left' as const,
                sorter: true,
              },
              {
                title: '上级代理id',
                dataIndex: 'superiorAgentId',
                key: 'superiorAgentId',
                width: 120,
                sorter: true,
              },
              {
                title: '顶层代理账号',
                dataIndex: 'topAgentAccount',
                key: 'topAgentAccount',
                width: 150,
                sorter: true,
              },
              {
                title: '顶层代理id',
                dataIndex: 'topAgentId',
                key: 'topAgentId',
                width: 120,
                sorter: true,
              },
              {
                title: '会员账号',
                dataIndex: 'memberAccount',
                key: 'memberAccount',
                width: 150,
                sorter: true,
              },
              {
                title: '会员id',
                dataIndex: 'memberId',
                key: 'memberId',
                width: 120,
                sorter: true,
              },
              {
                title: '会员属性',
                dataIndex: 'memberType',
                key: 'memberType',
                width: 100,
                sorter: true,
                render: (type: 'agent' | 'member') => {
                  return type === 'agent' ? (
                    <span style={{ color: '#1890ff' }}>代理</span>
                  ) : (
                    <span style={{ color: '#52c41a' }}>普通会员</span>
                  );
                },
              },
              {
                title: '注册时间',
                dataIndex: 'registerTime',
                key: 'registerTime',
                width: 180,
                sorter: true,
              },
              {
                title: '首充时间',
                dataIndex: 'firstDepositTime',
                key: 'firstDepositTime',
                width: 180,
                sorter: true,
                render: (text: string) => text || '-',
              },
              {
                title: (
                  <span>
                    本人总充值
                    <Tooltip title="代理本人账户的总充值金额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfTotalRecharge',
                key: 'selfTotalRecharge',
                width: 130,
                sorter: true,
                render: formatNumber,
              },
              {
                title: (
                  <span>
                    本人总提现
                    <Tooltip title="代理本人账户的总提现金额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfTotalWithdrawal',
                key: 'selfTotalWithdrawal',
                width: 130,
                sorter: true,
                render: formatNumber,
              },
              {
                title: (
                  <span>
                    本人总投注
                    <Tooltip title="代理本人账户的总投注金额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfTotalBet',
                key: 'selfTotalBet',
                width: 130,
                sorter: true,
                render: formatNumber,
              },
              {
                title: (
                  <span>
                    本人总输赢
                    <Tooltip title="代理本人账户的总输赢金额（正数为盈利，负数为亏损）">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfTotalWinLoss',
                key: 'selfTotalWinLoss',
                width: 130,
                sorter: true,
                render: (value: number) => {
                  const formatted = formatNumber(Math.abs(value));
                  return value >= 0 ? formatted : `-${formatted}`;
                },
              },
              {
                title: (
                  <span>
                    本人优惠
                    <Tooltip title="代理本人账户获得的优惠金额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfDiscount',
                key: 'selfDiscount',
                width: 130,
                sorter: true,
                render: formatNumber,
              },
              {
                title: (
                  <span>
                    本人返水
                    <Tooltip title="代理本人账户获得的返水金额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfRebate',
                key: 'selfRebate',
                width: 130,
                sorter: true,
                render: (value: number) => {
                  if (value === 0) return '0';
                  return value.toFixed(2);
                },
              },
              {
                title: (
                  <span>
                    本人获得返佣
                    <Tooltip title="代理本人账户获得的返佣金额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'selfCommissionReceived',
                key: 'selfCommissionReceived',
                width: 150,
                sorter: true,
                render: formatNumber,
              },
              {
                title: (
                  <span>
                    已发放佣金
                    <Tooltip title="已经发放给代理的佣金总额">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: 'issuedCommission',
                key: 'issuedCommission',
                width: 130,
                sorter: true,
                render: formatNumber,
              },
            ]}
            dataSource={modalDataSource}
            rowKey="id"
            loading={modalLoading}
            scroll={{ x: 1500 }}
            pagination={{
              current: modalPagination.current,
              pageSize: modalPagination.pageSize,
              total: modalPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共${total}条`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setModalPagination({
                  ...modalPagination,
                  current: page,
                  pageSize: pageSize || 10,
                });
              },
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AgentReportRefactored;

